import { useState, useEffect, useRef, useCallback } from "react";
import { useAuthContext } from "../../../../context/AuthContext";
import {
  connectiqProfileChat,
  connectiqGetProfileSessions,
  connectiqGetProfileHistory,
  connectiqCreateProfileSession,
} from "../../../../apis/connectiqAPI";

// ── Shared styles ─────────────────────────────────────────────────────────────

const BADGE_STYLES = {
  "Strong fit": { bg: "#eef9ee", color: "#2f7a3c" },
  "Good fit": { bg: "#fef4e8", color: "#b5750f" },
  "Partial fit": { bg: "#fef4e8", color: "#b5750f" },
  "Poor fit": { bg: "#fdeeee", color: "#b3402e" },
};

const STATUS_STYLES = {
  match: {
    dot: "#2f7a3c",
    pill: { bg: "#eef9ee", color: "#2f7a3c" },
    label: "Match",
  },
  gap: {
    dot: "#b3402e",
    pill: { bg: "#fdeeee", color: "#b3402e" },
    label: "Gap",
  },
  partial: {
    dot: "#b5750f",
    pill: { bg: "#fef4e8", color: "#b5750f" },
    label: "Partial",
  },
};

// ── Markdown renderer ─────────────────────────────────────────────────────────

const renderTextContent = (text) => {
  if (!text) return null;
  const lines = text.split("\n");
  const elements = [];
  let i = 0;

  const parseInline = (str) => {
    const parts = str.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, idx) => {
      if (p.startsWith("**") && p.endsWith("**"))
        return (
          <strong key={idx} style={{ fontWeight: "600", color: "#171717" }}>
            {p.slice(2, -2)}
          </strong>
        );
      const ip = p.split(/(\*[^*]+\*)/g);
      return ip.map((x, xi) =>
        x.startsWith("*") && x.endsWith("*") ? (
          <em key={xi} style={{ color: "#555" }}>
            {x.slice(1, -1)}
          </em>
        ) : (
          x
        ),
      );
    });
  };

  const isTableRow = (l) => l.trim().startsWith("|") && l.trim().endsWith("|");
  const isTableSep = (l) => /^\|[-| :]+\|$/.test(l.trim());

  while (i < lines.length) {
    const trimmed = lines[i].trim();

    if (trimmed === "---" || trimmed === "***") {
      elements.push(
        <div
          key={i}
          style={{ borderTop: "0.5px solid #e0e0e0", margin: "12px 0" }}
        />,
      );
      i++;
      continue;
    }
    if (isTableRow(trimmed)) {
      const tableLines = [];
      while (i < lines.length && isTableRow(lines[i].trim())) {
        tableLines.push(lines[i].trim());
        i++;
      }
      const rows = tableLines.filter((r) => !isTableSep(r));
      if (rows.length > 0) {
        const headers = rows[0]
          .split("|")
          .filter((c) => c.trim())
          .map((c) => c.trim());
        const bodyRows = rows.slice(1);
        elements.push(
          <div
            key={`t${i}`}
            style={{
              margin: "8px 0 10px",
              borderRadius: "8px",
              overflow: "hidden",
              border: "0.5px solid #e0e0e0",
            }}
          >
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "12.5px",
              }}
            >
              <thead>
                <tr>
                  {headers.map((h, hi) => (
                    <th
                      key={hi}
                      style={{
                        textAlign: "left",
                        padding: "8px 12px",
                        background: "#f3f3f3",
                        fontWeight: "500",
                        color: "#444",
                        borderBottom: "0.5px solid #e0e0e0",
                      }}
                    >
                      {parseInline(h)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {bodyRows.map((row, ri) => {
                  const cells = row
                    .split("|")
                    .filter((c) => c.trim())
                    .map((c) => c.trim());
                  return (
                    <tr
                      key={ri}
                      style={{ background: ri % 2 === 0 ? "#fff" : "#fafafa" }}
                    >
                      {cells.map((cell, ci) => (
                        <td
                          key={ci}
                          style={{
                            padding: "7px 12px",
                            borderBottom:
                              ri < bodyRows.length - 1
                                ? "0.5px solid #ececec"
                                : "none",
                            color: "#2b2b2b",
                            verticalAlign: "top",
                            lineHeight: "1.5",
                          }}
                        >
                          {parseInline(cell)}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>,
        );
      }
      continue;
    }
    if (trimmed.startsWith("## ")) {
      elements.push(
        <div
          key={i}
          style={{
            fontWeight: "600",
            fontSize: "13px",
            color: "#171717",
            marginTop: "14px",
            marginBottom: "5px",
            display: "flex",
            alignItems: "center",
            gap: "7px",
          }}
        >
          <div
            style={{
              width: "3px",
              height: "14px",
              background: "#6C63FF",
              borderRadius: "2px",
              flexShrink: 0,
            }}
          />
          {parseInline(trimmed.slice(3))}
        </div>,
      );
      i++;
      continue;
    }
    if (trimmed.startsWith("### ")) {
      elements.push(
        <div
          key={i}
          style={{
            fontWeight: "500",
            fontSize: "11px",
            color: "#444",
            marginTop: "10px",
            marginBottom: "3px",
            textTransform: "uppercase",
            letterSpacing: "0.04em",
          }}
        >
          {parseInline(trimmed.slice(4))}
        </div>,
      );
      i++;
      continue;
    }
    if (/^\d+\.\s/.test(trimmed)) {
      const num = trimmed.match(/^(\d+)\./)[1];
      elements.push(
        <div
          key={i}
          style={{
            display: "flex",
            gap: "10px",
            marginBottom: "5px",
            alignItems: "flex-start",
          }}
        >
          <div
            style={{
              width: "20px",
              height: "20px",
              borderRadius: "50%",
              background: "#f0eefe",
              color: "#6C63FF",
              fontSize: "11px",
              fontWeight: "600",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              marginTop: "1px",
            }}
          >
            {num}
          </div>
          <span
            style={{
              lineHeight: "1.65",
              fontSize: "13px",
              color: "#2b2b2b",
              flex: 1,
            }}
          >
            {parseInline(trimmed.replace(/^\d+\.\s/, ""))}
          </span>
        </div>,
      );
      i++;
      continue;
    }
    if (/^[-*]\s/.test(trimmed)) {
      elements.push(
        <div
          key={i}
          style={{
            display: "flex",
            gap: "9px",
            marginBottom: "4px",
            alignItems: "flex-start",
          }}
        >
          <div
            style={{
              width: "6px",
              height: "6px",
              borderRadius: "50%",
              background: "#6C63FF",
              flexShrink: 0,
              marginTop: "7px",
            }}
          />
          <span
            style={{ lineHeight: "1.65", fontSize: "13px", color: "#2b2b2b" }}
          >
            {parseInline(trimmed.slice(2))}
          </span>
        </div>,
      );
      i++;
      continue;
    }
    if (trimmed.startsWith("> ")) {
      elements.push(
        <div
          key={i}
          style={{
            borderLeft: "3px solid #6C63FF",
            paddingLeft: "10px",
            margin: "8px 0",
            color: "#555",
            fontSize: "12.5px",
            lineHeight: "1.6",
            background: "#f8f8ff",
            borderRadius: "0 6px 6px 0",
            padding: "6px 10px",
          }}
        >
          {parseInline(trimmed.slice(2))}
        </div>,
      );
      i++;
      continue;
    }
    if (trimmed === "") {
      elements.push(<div key={i} style={{ height: "5px" }} />);
      i++;
      continue;
    }
    elements.push(
      <p
        key={i}
        style={{
          margin: "2px 0",
          lineHeight: "1.65",
          fontSize: "13px",
          color: "#2b2b2b",
        }}
      >
        {parseInline(trimmed)}
      </p>,
    );
    i++;
  }
  return (
    <div
      style={{
        padding: "14px 16px",
        borderRadius: "4px 14px 14px 14px",
        background: "#f7f7f7",
        border: "0.5px solid #ececec",
      }}
    >
      {elements}
    </div>
  );
};

// ── Job Fit Result ────────────────────────────────────────────────────────────

const JobFitResult = ({ data }) => {
  const fitStyle = BADGE_STYLES[data.fit_label] || BADGE_STYLES["Partial fit"];
  const barColor =
    data.fit_score >= 70
      ? "#2f7a3c"
      : data.fit_score >= 45
        ? "#b5750f"
        : "#b3402e";
  return (
    <div style={{ fontSize: "13px" }}>
      <div
        style={{
          background: "#fff",
          border: "1px solid #171717",
          borderRadius: "12px",
          padding: "14px 18px",
          marginBottom: "8px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            marginBottom: data.summary ? "10px" : "0",
          }}
        >
          <div>
            <div
              style={{
                fontSize: "22px",
                fontWeight: "500",
                color: "#171717",
                lineHeight: 1,
              }}
            >
              {data.fit_score}%
            </div>
            <div
              style={{ fontSize: "10px", color: "#b8b8b8", marginTop: "2px" }}
            >
              match
            </div>
          </div>
          <div style={{ flex: 1 }}>
            <div
              style={{
                height: "6px",
                background: "#ececec",
                borderRadius: "3px",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  height: "100%",
                  width: `${data.fit_score}%`,
                  background: barColor,
                  borderRadius: "3px",
                }}
              />
            </div>
          </div>
          <span
            style={{
              fontSize: "10px",
              padding: "3px 9px",
              borderRadius: "100px",
              fontWeight: "500",
              background: fitStyle.bg,
              color: fitStyle.color,
              flexShrink: 0,
            }}
          >
            {data.fit_label}
          </span>
        </div>
        {data.summary && (
          <p
            style={{
              color: "#555",
              fontSize: "12.5px",
              lineHeight: "1.6",
              margin: 0,
            }}
          >
            {data.summary}
          </p>
        )}
      </div>
      {data.items?.length > 0 && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "6px",
            marginBottom: "8px",
          }}
        >
          {data.items.map((item, i) => {
            const s = STATUS_STYLES[item.status] || STATUS_STYLES.partial;
            return (
              <div
                key={i}
                style={{
                  background: "#fff",
                  border: "0.5px solid #ececec",
                  borderRadius: "12px",
                  padding: "10px 14px",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "10px",
                }}
              >
                <div
                  style={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    background: s.dot,
                    flexShrink: 0,
                    marginTop: "5px",
                  }}
                />
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontWeight: "500",
                      fontSize: "13px",
                      color: "#171717",
                      marginBottom: "2px",
                    }}
                  >
                    {item.label}
                  </div>
                  <div
                    style={{
                      fontSize: "11.5px",
                      color: "#999",
                      borderLeft: "2px solid #ececec",
                      paddingLeft: "9px",
                      lineHeight: "1.5",
                    }}
                  >
                    {item.detail}
                  </div>
                </div>
                <span
                  style={{
                    fontSize: "10px",
                    padding: "3px 9px",
                    borderRadius: "100px",
                    fontWeight: "500",
                    flexShrink: 0,
                    background: s.pill.bg,
                    color: s.pill.color,
                  }}
                >
                  {s.label}
                </span>
              </div>
            );
          })}
        </div>
      )}
      {data.has_gaps && data.training_plan?.length > 0 && (
        <>
          <div
            style={{
              fontSize: "11.5px",
              fontWeight: "500",
              color: "#555",
              margin: "10px 0 6px",
              paddingLeft: "2px",
            }}
          >
            Training plan to close the gaps
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            {data.training_plan.map((t, i) => (
              <div
                key={i}
                style={{
                  background: "#fff",
                  border: "0.5px solid #ececec",
                  borderRadius: "12px",
                  padding: "10px 14px",
                }}
              >
                <div
                  style={{
                    fontWeight: "500",
                    fontSize: "13px",
                    color: "#171717",
                    marginBottom: "3px",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  <span style={{ color: "#6C63FF" }}>📚</span>
                  {t.title}
                </div>
                <div
                  style={{
                    fontSize: "11.5px",
                    color: "#999",
                    borderLeft: "2px solid #ececec",
                    paddingLeft: "9px",
                    lineHeight: "1.5",
                  }}
                >
                  {t.detail}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// ── Helper ────────────────────────────────────────────────────────────────────

const timeAgo = (dateStr) => {
  if (!dateStr) return "";
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

// ── Main Component ────────────────────────────────────────────────────────────

const ConnectIQProfileTab = ({ resourceDetails }) => {
  const { userDetails } = useAuthContext();
  const parsedUser = userDetails ? JSON.parse(userDetails) : null;
  const userSlug = parsedUser?.user_slug || "";
  const userInitials = parsedUser?.user_name
    ? parsedUser.user_name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : "ME";

  const resourceSlug = resourceDetails?.profile_details?.user_slug;
  const candidateName =
    resourceDetails?.profile_details?.profile_name || "Candidate";

  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isInit, setIsInit] = useState(true);
  const [loadingMsgs, setLoadingMsgs] = useState(false);

  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  // Load session list on mount
  useEffect(() => {
    if (!userSlug || !resourceSlug) {
      setIsInit(false);
      return;
    }
    connectiqGetProfileSessions(userSlug, resourceSlug)
      .then((res) => {
        if (res?.data?.status === 1 && res.data.sessions?.length > 0) {
          setSessions(res.data.sessions);
          // Auto-open most recent session
          loadSession(res.data.sessions[0].session_id);
        }
        setIsInit(false);
      })
      .catch(() => setIsInit(false));
  }, [userSlug, resourceSlug]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height =
        Math.min(textareaRef.current.scrollHeight, 120) + "px";
    }
  }, [message]);

  const loadSession = (sid) => {
    setSessionId(sid);
    setMessages([]);
    setLoadingMsgs(true);
    connectiqGetProfileHistory(userSlug, sid)
      .then((res) => {
        if (res?.data?.status === 1) setMessages(res.data.messages || []);
        setLoadingMsgs(false);
      })
      .catch(() => setLoadingMsgs(false));
  };

  const handleNewChat = async () => {
    const res = await connectiqCreateProfileSession(
      userSlug,
      resourceSlug,
      candidateName,
    );
    if (res?.data?.status === 1) {
      const newSession = {
        session_id: res.data.session_id,
        session_title: "New chat",
        updated_at: new Date().toISOString(),
      };
      setSessions((prev) => [newSession, ...prev]);
      setSessionId(res.data.session_id);
      setMessages([]);
    }
  };

  const buildProfileSummary = () => {
    const p = resourceDetails?.profile_details || {};
    const emp = resourceDetails?.employment_details || [];
    const edu = resourceDetails?.education_details || [];
    let s = "";
    s += `Name: ${p.profile_name || "N/A"}\n`;
    s += `Current Role: ${p.profile_role || "N/A"}\n`;
    s += `Location: ${[p.city_name, p.state_name, p.country_name].filter(Boolean).join(", ") || "N/A"}\n`;
    s += `Availability: ${p.availability || 0} hrs/wk\n`;
    if (p.profile_bio) s += `Bio: ${p.profile_bio}\n`;
    if (p.skill_name) s += `Skills: ${p.skill_name}\n`;
    if (p.certifications) s += `Certifications: ${p.certifications}\n`;
    if (p.languages) s += `Languages: ${p.languages}\n`;
    if (edu.length > 0) {
      s += `\nEducation:\n`;
      edu.forEach((e) => {
        s += `  - ${e.degree_name || ""} in ${e.subject || ""} from ${e.school_name || ""} (${e.passed_on || ""})\n`;
      });
    }
    if (emp.length > 0) {
      s += `\nEmployment History:\n`;
      emp.forEach((e) => {
        const end = e.is_current == 1 ? "Present" : e.end_date || "Unknown";
        s += `  - ${e.job_title || ""} at ${e.employer_name || ""} (${e.start_date || "Unknown"} to ${end})\n`;
        if (e.description) s += `    ${e.description.substring(0, 300)}\n`;
      });
    }
    return s;
  };

  const doSend = useCallback(
    async (text, queryType = "general") => {
      if (!text || isLoading) return;
      let sid = sessionId;
      // Create session on first message if none exists
      if (!sid) {
        const res = await connectiqCreateProfileSession(
          userSlug,
          resourceSlug,
          candidateName,
        );
        if (res?.data?.status === 1) {
          sid = res.data.session_id;
          const newSession = {
            session_id: sid,
            session_title: text.length > 50 ? text.slice(0, 50) + "…" : text,
            updated_at: new Date().toISOString(),
          };
          setSessions((prev) => [newSession, ...prev]);
          setSessionId(sid);
        }
      }
      setMessages((prev) => [
        ...prev,
        { role: "user", type: "text", content: text },
      ]);
      setIsLoading(true);
      const res = await connectiqProfileChat(
        userSlug,
        text,
        buildProfileSummary(),
        resourceSlug,
        candidateName,
        sid,
        queryType,
      );
      if (res?.data?.status === 1) {
        // Update session title from first message
        setSessions((prev) =>
          prev.map((s) =>
            s.session_id === sid
              ? {
                  ...s,
                  session_title:
                    s.session_title === "New chat"
                      ? text.length > 50
                        ? text.slice(0, 50) + "…"
                        : text
                      : s.session_title,
                  updated_at: new Date().toISOString(),
                }
              : s,
          ),
        );
        if (res.data.type === "job_fit") {
          setMessages((prev) => [
            ...prev,
            { role: "assistant", type: "job_fit", data: res.data.data },
          ]);
        } else {
          setMessages((prev) => [
            ...prev,
            { role: "assistant", type: "text", content: res.data.message },
          ]);
        }
      } else {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            type: "text",
            content: "Something went wrong. Please try again.",
          },
        ]);
      }
      setIsLoading(false);
    },
    [isLoading, userSlug, resourceSlug, candidateName, sessionId],
  );

  const handleSend = useCallback(() => {
    const text = message.trim();
    if (!text) return;
    setMessage("");
    doSend(text, "general");
  }, [message, doSend]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (isInit) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          padding: "20px 0",
          color: "#8c8c8c",
          fontSize: "13px",
        }}
      >
        <div
          style={{
            width: "28px",
            height: "28px",
            borderRadius: "7px",
            background: "#171717",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            fontSize: "10px",
            fontWeight: "500",
          }}
        >
          IQ
        </div>
        Loading...
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        gap: "0",
        minHeight: "500px",
        border: "0.5px solid #ececec",
        borderRadius: "12px",
        overflow: "hidden",
        fontFamily: "inherit",
      }}
    >
      {/* ── Sidebar ── */}
      <div
        style={{
          width: "220px",
          flexShrink: 0,
          background: "#fff",
          borderRight: "0.5px solid #ececec",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Sidebar header */}
        <div
          style={{
            padding: "14px 12px 10px",
            borderBottom: "0.5px solid #ececec",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "7px",
              marginBottom: "10px",
            }}
          >
            <div
              style={{
                width: "22px",
                height: "22px",
                borderRadius: "5px",
                background: "#171717",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                fontSize: "9px",
                fontWeight: "600",
              }}
            >
              IQ
            </div>
            <span
              style={{
                fontSize: "12.5px",
                fontWeight: "500",
                color: "#171717",
              }}
            >
              ConnectIQ
            </span>
          </div>
          <button
            onClick={handleNewChat}
            style={{
              width: "100%",
              padding: "7px 10px",
              background: "#171717",
              color: "#fff",
              border: "none",
              borderRadius: "8px",
              fontSize: "12px",
              fontWeight: "500",
              cursor: "pointer",
              fontFamily: "inherit",
              textAlign: "left",
            }}
          >
            + New chat
          </button>
        </div>

        {/* Session list */}
        <div style={{ flex: 1, overflowY: "auto", padding: "8px 0" }}>
          {sessions.length === 0 ? (
            <div
              style={{
                padding: "12px",
                fontSize: "11.5px",
                color: "#b8b8b8",
                textAlign: "center",
              }}
            >
              No chats yet
            </div>
          ) : (
            sessions.map((s) => (
              <div
                key={s.session_id}
                onClick={() => loadSession(s.session_id)}
                style={{
                  padding: "8px 12px",
                  cursor: "pointer",
                  background:
                    sessionId === s.session_id ? "#f5f5f5" : "transparent",
                  borderLeft:
                    sessionId === s.session_id
                      ? "2px solid #171717"
                      : "2px solid transparent",
                  transition: "all 0.1s",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    marginBottom: "2px",
                  }}
                >
                  <div
                    style={{
                      width: "5px",
                      height: "5px",
                      borderRadius: "50%",
                      background: "#b8b8b8",
                      flexShrink: 0,
                    }}
                  />
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#171717",
                      fontWeight: sessionId === s.session_id ? "500" : "400",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      flex: 1,
                    }}
                  >
                    {s.session_title || "New chat"}
                  </div>
                </div>
                <div
                  style={{
                    fontSize: "10.5px",
                    color: "#b8b8b8",
                    paddingLeft: "11px",
                  }}
                >
                  {timeAgo(s.updated_at)}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* ── Chat area ── */}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          background: "#fff",
          minWidth: 0,
        }}
      >
        {/* Chat header */}
        <div
          style={{
            padding: "12px 16px",
            borderBottom: "0.5px solid #ececec",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <div
            style={{ fontSize: "13px", fontWeight: "500", color: "#171717" }}
          >
            {sessionId
              ? sessions.find((s) => s.session_id === sessionId)
                  ?.session_title || "New chat"
              : "Start a new chat"}
          </div>
          <div
            style={{ fontSize: "11.5px", color: "#b8b8b8", marginLeft: "auto" }}
          >
            {candidateName}
          </div>
        </div>

        {/* Messages */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "16px",
            display: "flex",
            flexDirection: "column",
            gap: "14px",
          }}
        >
          {!sessionId && messages.length === 0 && (
            <div
              style={{
                textAlign: "center",
                color: "#b8b8b8",
                fontSize: "13px",
                marginTop: "40px",
              }}
            >
              Ask anything about {candidateName}
            </div>
          )}

          {loadingMsgs && (
            <div
              style={{
                textAlign: "center",
                color: "#b8b8b8",
                fontSize: "12px",
              }}
            >
              Loading messages...
            </div>
          )}

          {messages.map((msg, i) => (
            <div key={i}>
              {msg.role === "user" ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "10px",
                    alignItems: "flex-start",
                  }}
                >
                  <div
                    style={{
                      maxWidth: "78%",
                      padding: "10px 14px",
                      borderRadius: "14px 4px 14px 14px",
                      background: "#171717",
                      color: "#fff",
                      fontSize: "13px",
                      lineHeight: "1.6",
                    }}
                  >
                    {msg.content}
                  </div>
                  <div
                    style={{
                      width: "28px",
                      height: "28px",
                      borderRadius: "50%",
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "10px",
                      fontWeight: "500",
                      background: "#f0f0f0",
                      color: "#6e6e6e",
                    }}
                  >
                    {userInitials}
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    display: "flex",
                    gap: "10px",
                    alignItems: "flex-start",
                  }}
                >
                  <div
                    style={{
                      width: "28px",
                      height: "28px",
                      borderRadius: "50%",
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "10px",
                      fontWeight: "500",
                      background: "#171717",
                      color: "#fff",
                    }}
                  >
                    IQ
                  </div>
                  <div style={{ flex: 1 }}>
                    {msg.type === "job_fit" ? (
                      <JobFitResult data={msg.data} />
                    ) : (
                      renderTextContent(msg.content)
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}

          {isLoading && (
            <div
              style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}
            >
              <div
                style={{
                  width: "28px",
                  height: "28px",
                  borderRadius: "50%",
                  background: "#171717",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "10px",
                  fontWeight: "500",
                  color: "#fff",
                  flexShrink: 0,
                }}
              >
                IQ
              </div>
              <div
                style={{
                  padding: "12px 16px",
                  background: "#f7f7f7",
                  borderRadius: "4px 14px 14px 14px",
                  display: "flex",
                  gap: "4px",
                  alignItems: "center",
                }}
              >
                {[0, 1, 2].map((j) => (
                  <div
                    key={j}
                    style={{
                      width: "6px",
                      height: "6px",
                      borderRadius: "50%",
                      background: "#c4c4c4",
                      animation: `ciq-bounce 1.2s ease-in-out ${j * 0.2}s infinite`,
                    }}
                  />
                ))}
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div style={{ padding: "12px 16px", borderTop: "0.5px solid #ececec" }}>
          <div
            style={{
              background: "#fff",
              borderRadius: "12px",
              display: "flex",
              alignItems: "flex-end",
              gap: "8px",
              padding: "10px 10px 10px 14px",
              border: "1px solid #d0d0d0",
            }}
          >
            <textarea
              ref={textareaRef}
              rows={1}
              placeholder={`Ask anything about ${candidateName}...`}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isLoading}
              style={{
                flex: 1,
                background: "transparent",
                border: "none",
                outline: "none",
                color: "#171717",
                fontSize: "13px",
                resize: "none",
                lineHeight: "1.5",
                maxHeight: "120px",
                fontFamily: "inherit",
              }}
            />
            <button
              onClick={handleSend}
              disabled={!message.trim() || isLoading}
              style={{
                width: "30px",
                height: "30px",
                borderRadius: "8px",
                background:
                  !message.trim() || isLoading ? "#e8e8e8" : "#171717",
                border: "none",
                color: !message.trim() || isLoading ? "#aaa" : "#fff",
                cursor:
                  !message.trim() || isLoading ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                fontSize: "14px",
              }}
            >
              ↑
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes ciq-bounce {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
          30% { transform: translateY(-5px); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default ConnectIQProfileTab;
